import { useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Image, ActivityIndicator } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import * as SMS from 'expo-sms';
import { getContentUriAsync } from 'expo-file-system/legacy';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import usePlatform from '@/utils/hooks/usePlatform';
import Unicon from './Unicon';
import RecipientList from './RecipientList';
import Callout from './Callout';
import DropdownMenu from '@/components/General/DropdownMenu';
import FilePickerModal from '@/components/Files/FilePickerModal';
import FileIcon from '@/components/Files/FileIcon';
import { sendSMS } from '@/data/sms';
import { smsSegments } from '@/utils/sms';
import { openURL } from '@/utils/linking';
import { choosePhoto, pickDocument, getFile, inferMimeType, formatSize } from '@/utils/FileManager';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';

const SMS_METHODS = [
    {
        id: 'native',
        name: 'Native Group Text',
        description: "Opens your device's Messages app. Sends from your personal number. Best for quick personal messages.",
        icon: 'chatbubble',
    },
    {
        id: 'native-individual',
        name: 'Native Individual Texts',
        description: 'Prefills Messages for each person, but you must tap Send for each text. Best for a handful of personal texts without a group message.',
        icon: 'chatbubbles',
    },
    {
        id: 'org',
        name: 'Organization SMS',
        description: 'Sends from your organization. Best for large groups or announcements.',
        icon: 'megaphone',
    },
];

const AVAILABLE_VARIABLES = [
    { key: '{{firstName}}', label: 'First Name' },
    { key: '{{lastName}}', label: 'Last Name' },
    { key: '{{fullName}}', label: 'Full Name' },
];

const getSmsPhone = (person) => person.Phones?.find(phone => phone.smsOptInStatus === 'opted_in')?.phoneNumber?.trim();

const getPersonalizedMessage = (person, message) => message
    .replaceAll('{{firstName}}', person.firstName || '')
    .replaceAll('{{lastName}}', person.lastName || '')
    .replaceAll('{{fullName}}', person.fullName || `${person.firstName || ''} ${person.lastName || ''}`.trim());

const SMSModal = ({ isOpen, onClose, recipients, recipientIDs = [] }) => {
    const { isNative, isAndroid } = usePlatform();
    const queryClient = useQueryClient();
    const { theme: { colors, fonts } } = useUnistyles();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: organization } = Query.organization.byId({ id: loggedInUser?.organizationID });
    const canSendOrganizationSMS = !!loggedInUser?.can?.('Send SMS Messages');
    const canSendNativeIndividualSMS = isNative && !!loggedInUser?.canAll?.(['View People', 'View Contact Info']);
    const canViewLibrary = !!loggedInUser?.can?.('View Library');
    const organizationSmsAvailable = !!organization?.enableSmsSending && canSendOrganizationSMS;
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [message, setMessage] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [filePickerOpen, setFilePickerOpen] = useState(false);
    const [isPreparingAttachment, setIsPreparingAttachment] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [nativeResult, setNativeResult] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [localRecipients, setLocalRecipients] = useState(recipients || []);
    const ContentScrollView = isNative ? KeyboardAwareScrollView : ScrollView;
    const contentScrollProps = isNative ? { bottomOffset: 20 } : {};

    useEffect(() => {
        if (isOpen) {
            setMessage('');
            setAttachment(null);
            setFilePickerOpen(false);
            setIsPreparingAttachment(false);
            setSendResult(null);
            setNativeResult(null);
            setIsSending(false);
            setLocalRecipients(recipients || []);
            if (!isNative && organizationSmsAvailable) {
                setSelectedMethod('org');
            } else {
                setSelectedMethod(null);
            }
        }
    }, [isOpen, isNative, recipients, organizationSmsAvailable]);

    // Stable identity so memoized RecipientList does not re-render per keystroke.
    const handleRemoveRecipient = useCallback((id) => {
        setLocalRecipients(prev => prev.filter(r => r.id !== id));
    }, []);

    const nativeIndividualRecipients = useMemo(() => {
        const seenPhones = new Set();
        return localRecipients
            .map(person => {
                const phone = getSmsPhone(person);
                const phoneKey = phone?.replace(/[^\d+]/g, '');
                if (!phone || !phoneKey || seenPhones.has(phoneKey)) return null;
                seenPhones.add(phoneKey);
                return { person, phone };
            })
            .filter(Boolean);
    }, [localRecipients]);

    const nativeSkippedCount = localRecipients.length - nativeIndividualRecipients.length;

    const handleNativeText = () => {
        const numbers = localRecipients
            .map(getSmsPhone)
            .filter(Boolean)
            .join(',');
        if (!numbers) {
            return;
        }
        openURL(`sms:${numbers}`);
        onClose();
    };

    const handlePickPhoto = async () => {
        try {
            // Images only — ImageManipulator compression keeps files under MMS
            // carrier limits (~1MB); videos would not be compressed.
            const photos = await choosePhoto({ noVideos: true });
            const photo = photos?.[0];
            if (!photo) return;
            setAttachment({
                uri: photo.uri,
                name: photo.name,
                size: photo.size || 0,
                mimeType: inferMimeType(photo.name) || inferMimeType(photo.uri) || 'image/jpeg',
                thumbnailUri: photo.thumbnailUri || photo.uri,
            });
        } catch {
            Toast.show({ type: 'error', text2: 'Could not attach that photo.' });
        }
    };

    const handlePickDocument = async () => {
        try {
            const docs = await pickDocument({ multiple: false });
            const doc = docs?.[0];
            if (!doc) return;
            setAttachment({
                uri: doc.uri,
                name: doc.name,
                size: doc.size || 0,
                mimeType: doc.mimeType || 'application/octet-stream',
                thumbnailUri: null,
            });
        } catch {
            Toast.show({ type: 'error', text2: 'Could not attach that file.' });
        }
    };

    const handleReachFileSelect = async (file) => {
        setIsPreparingAttachment(true);
        try {
            // getFile downloads from Supabase storage to the device if not cached
            const uri = await getFile(file.key);
            if (!uri || typeof uri !== 'string') {
                Toast.show({ type: 'error', text2: 'Could not download the selected file.' });
                return;
            }
            setAttachment({
                uri,
                name: file.filename,
                size: file.size || 0,
                mimeType: file.mimeType || inferMimeType(file.filename) || 'application/octet-stream',
                thumbnailUri: file.mimeType?.startsWith('image/') ? uri : null,
            });
        } catch {
            Toast.show({ type: 'error', text2: 'Could not download the selected file.' });
        } finally {
            setIsPreparingAttachment(false);
        }
    };

    const attachOptions = [
        { name: 'Photo Library', icon: { web: 'image-outline', ios: 'photo', android: 'photo_library' }, onSelect: handlePickPhoto },
        { name: 'Phone Files', icon: { web: 'document-outline', ios: 'doc', android: 'insert_drive_file' }, onSelect: handlePickDocument },
        ...(canViewLibrary ? [{ name: 'Reach Files', icon: { web: 'folder-outline', ios: 'folder', android: 'folder' }, onSelect: () => setFilePickerOpen(true) }] : []),
    ];

    const handleNativeIndividualSMS = async () => {
        const body = message.trim();
        if (!body) {
            Toast.show({ type: 'error', text2: 'Please type a message before sending.' });
            return;
        }
        if (!canSendNativeIndividualSMS) {
            Toast.show({ type: 'error', text2: 'You do not have permission to send individual texts.' });
            return;
        }
        if (nativeIndividualRecipients.length === 0) {
            Toast.show({ type: 'error', text2: 'No recipients have an SMS-enabled phone number.' });
            return;
        }

        setIsSending(true);
        const result = {
            attemptedCount: 0,
            sentCount: 0,
            cancelledCount: 0,
            unknownCount: 0,
            failedCount: 0,
            skippedCount: nativeSkippedCount,
            totalCount: localRecipients.length,
            failedRecipients: [],
        };

        try {
            const isAvailable = await SMS.isAvailableAsync();
            if (!isAvailable) {
                Toast.show({ type: 'error', text2: 'SMS is not available on this device.' });
                return;
            }

            // Android's messaging intent only accepts content:// attachment URIs;
            // convert the local file:// path once and reuse it for every recipient.
            // iOS takes the file:// URI as-is. expo-sms supports max 1 attachment.
            let smsOptions;
            if (attachment?.uri) {
                let attachmentUri = attachment.uri;
                if (isAndroid) {
                    try {
                        attachmentUri = await getContentUriAsync(attachment.uri);
                    } catch {
                        Toast.show({ type: 'error', text2: 'Could not prepare the attachment for sending.' });
                        return;
                    }
                }
                smsOptions = {
                    attachments: {
                        uri: attachmentUri,
                        mimeType: attachment.mimeType,
                        filename: attachment.name,
                    },
                };
            }

            for (const target of nativeIndividualRecipients) {
                try {
                    const response = await SMS.sendSMSAsync(
                        target.phone,
                        getPersonalizedMessage(target.person, body),
                        smsOptions,
                    );
                    result.attemptedCount += 1;
                    if (response.result === 'sent') result.sentCount += 1;
                    else if (response.result === 'cancelled') result.cancelledCount += 1;
                    else result.unknownCount += 1;
                } catch {
                    const name = target.person.fullName || `${target.person.firstName || ''} ${target.person.lastName || ''}`.trim() || 'Unknown';
                    result.failedCount += 1;
                    result.failedRecipients.push({ name, phone: target.phone });
                    break;
                }
            }
            setNativeResult(result);
        } catch {
            Toast.show({ type: 'error', text2: 'Could not open the Messages app on this device.' });
        } finally {
            setIsSending(false);
        }
    };

    const handleOrgSMS = async () => {
        if (!message.trim()) {
            Toast.show({ type: 'error', text2: 'Please type a message before sending.' });
            return;
        }

        const uniqueRecipientIDs = [...new Set(recipientIDs.filter(Boolean))];

        if (uniqueRecipientIDs.length === 0) {
            Toast.show({ type: 'error', text2: 'No valid recipients were selected.' });
            return;
        }

        setIsSending(true);
        try {
            const result = await sendSMS({ recipientIDs: uniqueRecipientIDs, message: message.trim() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.smsCampaigns.all() });
            setSendResult(result);
        } catch {
            // sendSMS already shows a Toast on error
        } finally {
            setIsSending(false);
        }
    };

    const insertVariable = (variable) => {
        setMessage(prev => prev + variable);
    };

    // Billed-segment estimate. Org sends prepend "[Org]: " and merge names per
    // recipient, so segment counts vary — compute per unique phone number and
    // sum, matching how Telnyx bills.
    const orgPrefix = selectedMethod === 'org' ? `[${organization?.name || ''}]: ` : '';
    const segmentInfo = useMemo(() => {
        const template = smsSegments(orgPrefix + message);
        if (selectedMethod === 'native') {
            return { ...template, counted: 1, totalSegments: template.segments, maxSegments: template.segments };
        }
        const targets = selectedMethod === 'org'
            ? localRecipients.filter(person => getSmsPhone(person))
            : nativeIndividualRecipients.map(t => t.person);
        const seenPhones = new Set();
        const unique = targets.filter(person => {
            const key = getSmsPhone(person)?.replace(/[^\d+]/g, '');
            if (!key || seenPhones.has(key)) return false;
            seenPhones.add(key);
            return true;
        });
        let totalSegments = 0;
        let maxSegments = 0;
        for (const person of unique) {
            const count = smsSegments(orgPrefix + getPersonalizedMessage(person, message)).segments;
            totalSegments += count;
            if (count > maxSegments) maxSegments = count;
        }
        return { ...template, counted: unique.length, totalSegments, maxSegments };
    }, [orgPrefix, message, selectedMethod, localRecipients, nativeIndividualRecipients]);

    const availableMethods = useMemo(() => SMS_METHODS.filter(method => {
        if (method.id === 'org') return organizationSmsAvailable;
        if (method.id === 'native-individual') return canSendNativeIndividualSMS;
        return isNative;
    }), [organizationSmsAvailable, canSendNativeIndividualSMS, isNative]);

    const renderMethodSelection = () => (
        <ContentScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled" {...contentScrollProps}
>
            <RecipientList recipients={localRecipients} mode="sms" onRemove={handleRemoveRecipient} />

            <View style={styles.methodsContainer}>
                {availableMethods.length === 0 && (
                    <Callout type="warning" message="No SMS sending methods are available for this account." />
                )}
                {availableMethods.map((method) => {
                    const isDisabled = (method.id === 'native' && localRecipients.length > 20)
                        || (method.id === 'native-individual' && nativeIndividualRecipients.length === 0);
                    const disabledReason = method.id === 'native' ? 'too many recipients' : 'no eligible numbers';
                    return (
                        <TouchableOpacity
                            key={method.id}
                            testID={`sms-method-${method.id}`}
                            style={[styles.methodCard, isDisabled && styles.methodCardDisabled]}
                            onPress={() => {
                                if (isDisabled) return;
                                setSelectedMethod(method.id);
                                if (method.id === 'native') {
                                    handleNativeText();
                                }
                            }}
                            disabled={isDisabled}
                        >
                            <View style={[styles.methodIcon, isDisabled && styles.methodIconDisabled]}>
                                <Unicon name={method.icon} size={24} colorVariant={isDisabled ? 'textTertiary' : 'primary'} />
                            </View>
                            <View style={styles.methodContent}>
                                <Text style={[styles.methodName, { fontFamily: fonts.heading, color: isDisabled ? colors.textTertiary : colors.text }]}>
                                    {method.name}
                                    {isDisabled && (
                                        <Text style={{ fontFamily: fonts.body, color: colors.textTertiary }}> — {disabledReason}</Text>
                                    )}
                                </Text>
                                <Text style={[styles.methodDescription, { fontFamily: fonts.body, color: isDisabled ? colors.textTertiary : colors.textSecondary }]}>
                                    {method.description}
                                </Text>
                            </View>
                            {!isDisabled && <Unicon name="chevron-forward" size={18} colorVariant="textSecondary" />}
                        </TouchableOpacity>
                    );
                })}
            </View>
        </ContentScrollView>
    );

    const renderComposeForm = () => (
        <ContentScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled" {...contentScrollProps}
>
            <TouchableOpacity style={styles.backButton} onPress={() => setSelectedMethod(null)}>
                <Unicon name="chevron-back" size={20} colorVariant="primary" />
                <Text style={[styles.backButtonText, { fontFamily: fonts.heading, color: colors.primary }]}>
                    Back to methods
                </Text>
            </TouchableOpacity>

            <RecipientList recipients={localRecipients} mode="sms" onRemove={handleRemoveRecipient} deduplicateContacts={selectedMethod === 'native-individual'} />

            {selectedMethod === 'org' && (() => {
                const eligibleCount = localRecipients.filter(p => p.Phones?.some(ph => ph.smsOptInStatus === 'opted_in')).length;
                const skippedCount = localRecipients.length - eligibleCount;
                if (skippedCount === 0) return null;
                return (
                    <Callout
                        type="warning"
                        message={`Only ${eligibleCount} of ${localRecipients.length} ${localRecipients.length === 1 ? 'recipient' : 'recipients'} will receive this message (${skippedCount} ${skippedCount === 1 ? 'person does' : 'people do'} not have SMS enabled)`}
                    />
                );
            })()}

            {selectedMethod === 'native-individual' && (
                <Callout
                    type="warning"
                    message={`Reach opens a prefilled message for each eligible recipient. You must tap Send for each text.${nativeSkippedCount > 0 ? ` ${nativeSkippedCount} ${nativeSkippedCount === 1 ? 'person is' : 'people are'} skipped.` : ''}`}
                />
            )}

            <View style={styles.bodySection}>
                <View style={styles.bodyHeader}>
                    <Text style={[styles.bodyTitle, { fontFamily: fonts.heading, color: colors.text }]}>
                        Message
                    </Text>
                    <Text style={[styles.bodyHint, { fontFamily: fonts.body, color: colors.textTertiary }]}>
                        You can use variables like {'{{firstName}}'} that will be replaced with recipient data
                    </Text>
                </View>

                <Input
                    multiline
                    value={message}
                    onUpdate={setMessage}
                    placeholder="Write your SMS message here..."
                    style={styles.bodyInput}
                />

                <View style={styles.counterRow}>
                    <Text style={[styles.counterText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                        {message.length} chars · {segmentInfo.counted > 1 ? 'up to ' : ''}{segmentInfo.maxSegments} segment{segmentInfo.maxSegments > 1 ? 's' : ''}/message
                        {segmentInfo.counted > 1 && ` · ≈${segmentInfo.totalSegments} billed for ${segmentInfo.counted} recipients`}
                    </Text>
                </View>

                {segmentInfo.encoding === 'Unicode' && (
                    <Callout
                        type="warning"
                        message="Emoji or special characters detected — these switch the message to a 70-char segment limit (67 when split), increasing billed segments."
                    />
                )}

                {segmentInfo.maxSegments > 1 && (
                    <Callout
                        type="warning"
                        message="Multi-segment message — each segment is billed separately."
                    />
                )}

                {selectedMethod === 'native-individual' && (
                    <View style={styles.attachSection}>
                        {isPreparingAttachment ? (
                            <View style={styles.attachmentChip}>
                                <ActivityIndicator size="small" color={colors.primary} />
                                <Text style={[styles.attachmentMeta, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                                    Preparing attachment...
                                </Text>
                            </View>
                        ) : attachment ? (
                            <View testID="sms-attachment-chip" style={styles.attachmentChip}>
                                {attachment.thumbnailUri ? (
                                    <Image source={{ uri: attachment.thumbnailUri }} style={styles.attachmentThumb} />
                                ) : (
                                    <FileIcon filename={attachment.name} mimeType={attachment.mimeType} size={22} color="textSecondary" />
                                )}
                                <View style={styles.attachmentInfo}>
                                    <Text numberOfLines={1} style={[styles.attachmentName, { fontFamily: fonts.body, color: colors.text }]}>
                                        {attachment.name}
                                    </Text>
                                    <Text style={[styles.attachmentMeta, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                                        {formatSize(attachment.size || 0)} · sends as MMS
                                    </Text>
                                </View>
                                <TouchableOpacity testID="sms-attachment-remove" hitSlop={8} style={styles.attachmentRemove} onPress={() => setAttachment(null)}>
                                    <Unicon name="close" size={16} colorVariant="textSecondary" />
                                </TouchableOpacity>
                            </View>
                        ) : (
                            <DropdownMenu
                                testID="sms-attach"
                                align="start"
                                triggerComponent={
                                    <View style={styles.attachButton}>
                                        <Unicon name="attach-outline" size={16} colorVariant="primary" />
                                        <Text style={[styles.attachButtonText, { fontFamily: fonts.heading, color: colors.primary }]}>
                                            Attach File (MMS)
                                        </Text>
                                    </View>
                                }
                                options={attachOptions}
                            />
                        )}
                    </View>
                )}

                <View style={styles.variablesSection}>
                    <Text style={[styles.variablesTitle, { fontFamily: fonts.heading, color: colors.text }]}>
                        Available Variables
                    </Text>
                    <View style={styles.variablesList}>
                        {AVAILABLE_VARIABLES.map((variable) => (
                            <TouchableOpacity
                                key={variable.key}
                                style={styles.variableChip}
                                onPress={() => insertVariable(variable.key)}
                            >
                                <Text style={[styles.variableKey, { fontFamily: fonts.bold, color: colors.primary }]}>
                                    {variable.key}
                                </Text>
                                <Text style={[styles.variableLabel, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                                    {variable.label}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>
                </View>
            </View>
        </ContentScrollView>
    );

    const renderNativeResults = () => {
        const unattemptedCount = Math.max(
            (nativeResult?.totalCount ?? 0)
            - (nativeResult?.skippedCount ?? 0)
            - (nativeResult?.attemptedCount ?? 0)
            - (nativeResult?.failedCount ?? 0),
            0,
        );
        const incompleteCount = (nativeResult?.failedCount ?? 0)
            + (nativeResult?.cancelledCount ?? 0)
            + unattemptedCount;
        return (
            <ContentScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled" {...contentScrollProps}
>
                <View style={styles.resultsHeader}>
                    <Unicon name="checkmark-circle" size={32} colorVariant={incompleteCount > 0 ? 'warning' : nativeResult?.attemptedCount > 0 ? 'primary' : 'textTertiary'} />
                    <Text style={[styles.resultsTitle, { fontFamily: fonts.heading, color: colors.text }]}>
                        {incompleteCount > 0 ? 'Native Texts Incomplete' : 'Native Texts Complete'}
                    </Text>
                </View>

                <View style={styles.resultsStats}>
                    <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                        Opened Messages: {nativeResult?.attemptedCount ?? 0}
                    </Text>
                    <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                        Marked sent by device: {nativeResult?.sentCount ?? 0}
                    </Text>
                    {nativeResult?.unknownCount > 0 && (
                        <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                            Status not reported: {nativeResult.unknownCount}
                        </Text>
                    )}
                    {nativeResult?.cancelledCount > 0 && (
                        <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                            Cancelled: {nativeResult.cancelledCount}
                        </Text>
                    )}
                    {nativeResult?.skippedCount > 0 && (
                        <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                            Skipped: {nativeResult.skippedCount}
                        </Text>
                    )}
                    {unattemptedCount > 0 && (
                        <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                            Not opened: {unattemptedCount}
                        </Text>
                    )}
                    {nativeResult?.failedCount > 0 && (
                        <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.error }]}>
                            Failed to open: {nativeResult.failedCount}
                        </Text>
                    )}
                </View>

                {nativeResult?.failedRecipients?.length > 0 && (
                    <View style={styles.failedSection}>
                        <Text style={[styles.failedTitle, { fontFamily: fonts.heading, color: colors.error }]}>
                            Failed Recipients
                        </Text>
                        {nativeResult.failedRecipients.map((r, i) => (
                            <View key={i} style={styles.failedRow}>
                                <Text style={[styles.failedName, { fontFamily: fonts.body, color: colors.text }]}>
                                    {r.name}
                                </Text>
                                <Text style={[styles.failedPhone, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                                    {r.phone}
                                </Text>
                            </View>
                        ))}
                    </View>
                )}
            </ContentScrollView>
        );
    };

    const renderResults = () => (
        <ContentScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled" {...contentScrollProps}
>
            <View style={styles.resultsHeader}>
                <Unicon name="checkmark-circle" size={32} colorVariant={sendResult?.timedOut ? 'warning' : sendResult?.sentCount > 0 ? 'primary' : 'textTertiary'} />
                <Text style={[styles.resultsTitle, { fontFamily: fonts.heading, color: colors.text }]}>
                    {sendResult?.timedOut ? 'Still Processing' : sendResult?.sentCount > 0 ? 'SMS Sent' : 'SMS Failed'}
                </Text>
            </View>

            {sendResult?.timedOut ? (
                <View style={styles.resultsStats}>
                    <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                        Your SMS campaign is still sending in the background. Check the campaign history for final results.
                    </Text>
                </View>
            ) : (
            <>
            <View style={styles.resultsStats}>
                <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                    Sent: {sendResult?.sentCount ?? 0}
                </Text>
                {sendResult?.skippedCount > 0 && (
                    <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                        Skipped (no SMS number): {sendResult.skippedCount}
                    </Text>
                )}
                {sendResult?.failedCount > 0 && (
                    <Text style={[styles.statText, { fontFamily: fonts.body, color: colors.error }]}>
                        Failed: {sendResult.failedCount}
                    </Text>
                )}
            </View>

            {sendResult?.failedRecipients?.length > 0 && (
                <View style={styles.failedSection}>
                    <Text style={[styles.failedTitle, { fontFamily: fonts.heading, color: colors.error }]}>
                        Failed Recipients
                    </Text>
                    {sendResult.failedRecipients.map((r, i) => (
                        <View key={i} style={styles.failedRow}>
                            <Text style={[styles.failedName, { fontFamily: fonts.body, color: colors.text }]}>
                                {r.name}
                            </Text>
                            <Text style={[styles.failedPhone, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                                {r.phone}
                            </Text>
                        </View>
                    ))}
                </View>
            )}
            </>
            )}
        </ContentScrollView>
    );

    const getConfirmText = () => {
        if (sendResult || nativeResult) return 'Close';
        if (selectedMethod === 'org') return isSending ? 'Sending...' : 'Send';
        if (selectedMethod === 'native-individual') return isSending ? 'Opening Messages...' : 'Send Texts';
        return undefined;
    };

    const getOnConfirm = () => {
        if (sendResult || nativeResult) return onClose;
        if (selectedMethod === 'org') return handleOrgSMS;
        if (selectedMethod === 'native-individual') return handleNativeIndividualSMS;
        return undefined;
    };

    return (
        <>
            <SheetModal
                isOpen={isOpen}
                onClose={onClose}
                title="Send SMS"
                cancelText="Cancel"
                confirmText={getConfirmText()}
                onConfirm={getOnConfirm()}
                loading={isSending}
                scrollable={false}
            >
                {sendResult || nativeResult
                    ? nativeResult ? renderNativeResults() : renderResults()
                    : selectedMethod === null
                        ? renderMethodSelection()
                        : renderComposeForm()}
            </SheetModal>

            <FilePickerModal
                isOpen={filePickerOpen}
                onClose={() => setFilePickerOpen(false)}
                onSelect={handleReachFileSelect}
                scope="resources"
                ownerID={null}
                title="Select a file"
            />
        </>
    );
};

const styles = StyleSheet.create(({ colors }) => ({
    methodsContainer: {
        gap: 12,
        marginTop: 16,
    },
    methodCard: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        backgroundColor: colors.surface,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border,
        gap: 12,
        _web: {
            _hover: {
                backgroundColor: colors.border,
            },
        },
    },
    methodIcon: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    methodIconDisabled: {
        backgroundColor: colors.background,
    },
    methodCardDisabled: {
        backgroundColor: colors.background,
        borderColor: colors.border,
        opacity: 0.6,
    },
    methodContent: {
        flex: 1,
    },
    methodName: {
        fontSize: 16,
        marginBottom: 4,
    },
    methodDescription: {
        fontSize: 13,
        lineHeight: 18,
    },
    backButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginBottom: 20,
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: colors.surface,
        borderRadius: 8,
        alignSelf: 'flex-start',
        _web: {
            transition: 'background-color 0.2s',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    backButtonText: {
        fontSize: 14,
    },
    bodySection: {
        marginTop: 16,
    },
    bodyHeader: {
        marginBottom: 8,
    },
    bodyTitle: {
        fontSize: 14,
        marginBottom: 4,
    },
    bodyHint: {
        fontSize: 12,
        fontStyle: 'italic',
    },
    bodyInput: {
        minHeight: 100,
    },
    counterRow: {
        marginTop: 8,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 4,
    },
    counterText: {
        fontSize: 12,
    },
    attachSection: {
        marginTop: 16,
    },
    attachButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        alignSelf: 'flex-start',
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: colors.surface,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: colors.border,
    },
    attachButtonText: {
        fontSize: 13,
    },
    attachmentChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 10,
        backgroundColor: colors.surface,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.border,
    },
    attachmentThumb: {
        width: 32,
        height: 32,
        borderRadius: 6,
    },
    attachmentInfo: {
        flex: 1,
    },
    attachmentName: {
        fontSize: 13,
    },
    attachmentMeta: {
        fontSize: 11,
    },
    attachmentRemove: {
        padding: 4,
    },
    variablesSection: {
        marginTop: 16,
    },
    variablesTitle: {
        fontSize: 14,
        marginBottom: 12,
    },
    variablesList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    variableChip: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: colors.surface,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: colors.border,
        gap: 6,
    },
    variableKey: {
        fontSize: 12,
    },
    variableLabel: {
        fontSize: 12,
    },
    resultsHeader: {
        alignItems: 'center',
        paddingVertical: 20,
        gap: 12,
    },
    resultsTitle: {
        fontSize: 20,
    },
    resultsStats: {
        gap: 8,
        paddingHorizontal: 12,
        paddingBottom: 16,
    },
    statText: {
        fontSize: 14,
    },
    failedSection: {
        marginTop: 8,
        paddingHorizontal: 12,
    },
    failedTitle: {
        fontSize: 14,
        marginBottom: 8,
    },
    failedRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    failedName: {
        fontSize: 13,
    },
    failedPhone: {
        fontSize: 13,
    },
}));

export default SMSModal;
